import { useState, useCallback } from "react";
import { usersService } from "../services/usersService";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";

/**
 * Custom hook for user management
 * Handles user-related operations and state management
 */
export const useUserManagement = () => {
  const { authFetch } = useAuth();
  const { notifySuccess, notifyError } = useNotification();

  const [usersData, setUsersData] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    results: [],
  });
  const [loading, setLoading] = useState(false);

  /**
   * Load users data with pagination
   */
  const loadUsers = useCallback(
    async (page = 1, perPage = 10) => {
      try {
        setLoading(true);
        const response = await usersService.getUsersOrgsRoles(
          authFetch,
          page,
          perPage,
        );
        setUsersData(response.data);
        return response.data;
      } catch (error) {
        if (error?.response?.status === 401) {
          notifyError("Unauthorized access. Please log in again.");
        } else if (error?.response?.status === 403) {
          notifyError(
            "Access forbidden. You do not have permission to view this data.",
          );
        } else {
          const errorMessage = error?.response?.data?.message || error.message;
          console.error("Error loading users:", error);
          notifyError(`Failed to load users: ${errorMessage}`);
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, notifyError],
  );

  /**
   * Set user password (admin only)
   */
  const setUserPassword = useCallback(
    async (username, password) => {
      try {
        await usersService.setUserPassword(username, password, authFetch);
        notifySuccess("Password updated successfully");
        return true;
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error.message;
        console.error("Error setting user password:", error);
        notifyError(`Failed to update password: ${errorMessage}`);
        return false;
      }
    },
    [authFetch, notifySuccess, notifyError],
  );

  /**
   * Grant or revoke super admin (admin only)
   */
  const setUserSuperAdmin = useCallback(
    async (username, isSuperuser) => {
      try {
        await usersService.updateUser(
          username,
          { is_superuser: isSuperuser },
          authFetch,
        );
        notifySuccess(
          isSuperuser
            ? `Granted Super Admin to "${username}"`
            : `Revoked Super Admin from "${username}"`,
        );
        await loadUsers(usersData.page, usersData.per_page);
        return true;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error.message;
        console.error("Error updating super admin flag:", error);
        notifyError(`Failed to update Super Admin: ${errorMessage}`);
        return false;
      }
    },
    [
      authFetch,
      notifySuccess,
      notifyError,
      loadUsers,
      usersData.page,
      usersData.per_page,
    ],
  );

  return {
    // State
    usersData,
    loading,

    // Methods
    loadUsers,
    setUserPassword,
    setUserSuperAdmin,

    // Pagination helpers
    pagination: {
      current: usersData.page,
      pageSize: usersData.per_page,
      total: usersData.total,
      onChange: (page, pageSize) => loadUsers(page, pageSize),
    },
  };
};
